import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Button, Dropdown, Spinner, SplitButton } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import useAlert from '../../../Alerts/useAlert';
import usePrivileges from '../../../Auth/usePrivileges';
import { useHttpUtils } from '../../../Utilities/useHttpUtils';
import { useSiteCode } from '../../../Utilities/useSiteCode';
import { LotDetailContext } from '../../../Contexts/LotDetailContext';

function ViewReportButton() {

    const { t } = useTranslation();
    const { setAlert } = useAlert();
    const { fetchBlobAuthenticated, downloadFile } = useHttpUtils();

    const {
        lotDetail: {
            summary: {
                lotNumber,
                businessUnitCode,
                lotRevisionId,
                currentStatus,
                processingStatus
            }
        }
    } = useContext(LotDetailContext);

    const { admin } = usePrivileges();
    const siteCode = useSiteCode();

    const [disabled, setDisabled] = useState<boolean>(false);
    const [loadingReport, setLoadingReport] = useState<boolean>(false);
    const [reportItems, setReportItems] = useState<any[]>([]);

    useEffect(() => {
        switch (processingStatus) {
            case "Processing":
            case "Queued":
                setDisabled(true);
                break;
            default:
                switch (currentStatus) {
                    default:
                        setDisabled(false);
                }
        }
    }, [currentStatus, processingStatus]);

    const viewLotReport = useCallback(async () => {
        setLoadingReport(true);

        let url = `api/v1/lot/${lotNumber}/report`;
        if (lotRevisionId) {
            url += `?revisionId=${lotRevisionId}`;
        }

        url += `&regenerateReport=true`;

        await fetchBlobAuthenticated(url)
            .then(async response => {
                if (response.ok) {
                    return response.blob();
                } else {
                    throw new Error(await response.text());
                }
            })
            .then((response) => {
                return response = response.slice(0, response.size, "application/pdf");
            })
            .then(blob => {
                return URL.createObjectURL(blob);
            })
            .then(url => window.open(url))
            .catch(error => {
                setAlert("danger", "Error", error.message);
            })
            .finally(() => {
                setLoadingReport(false);
            });
    }, [fetchBlobAuthenticated, lotNumber, lotRevisionId, setAlert]);

    const viewBaggingReport = useCallback(async () => {
        window.open(`${window.location.origin}/lotdetails/${lotNumber}/baggingReport`, '_blank', 'noreferrer');
        return;
    }, [lotNumber]);

    const viewLotDetailsReport = useCallback(async () => {
        window.open(`${window.location.origin}/lotdetails/${lotNumber}/detailsReport`, '_blank', 'noreferrer');
        return;
    }, [lotNumber]);

    const viewWilsonWarehouseReport = useCallback(async () => {
        setLoadingReport(true);
        let url = `api/v1/lot/${lotNumber}/wilsonReport`;
        var fileName = "";
        var contentType = "";

        await fetchBlobAuthenticated(url)
            .then(async response => {
                if (response.ok) {
                    fileName = response.headers.get("Content-Disposition")?.split("filename=")[1] || `${lotNumber}.txt`;
                    contentType = response.headers.get("Content-Type") || "text/plain";

                    return response.blob();
                } else {
                    throw new Error(await response.text());
                }
            })
            .then((response) => {
                return response = response.slice(0, response.size, contentType);
            })
            .then(blob => {
                downloadFile(blob, fileName);
            })
            .catch(error => {
                setAlert("danger", "Error", error.message);
            })
            .finally(() => {
                setLoadingReport(false);
            });
    }, [downloadFile, fetchBlobAuthenticated, lotNumber, setAlert]);

    const viewDailyPackageReport = useCallback(async () => {
        window.open(`${window.location.origin}/lotdetails/${lotNumber}/packageReport`, '_blank', 'noreferrer');
        return;
    }, [lotNumber]);

    const viewLotDataReport = useCallback(async () => {
        window.open(`${window.location.origin}/lotdetails/${lotNumber}/lotProcessDataReport`, '_blank', 'noreferrer');
        return;
    }, [lotNumber]);

    useEffect(() => {
        const viewReportItem = { name: 'LotDetails.Summary.ViewReport', onClick: () => viewLotReport(false) };
        const baggingReportItem = { name: 'LotDetails.Summary.ViewLotSplitReport', onClick: () => viewBaggingReport() };
        const viewLotDetailsReportItem = { name: 'LotDetails.Summary.ViewReport', onClick: () => viewLotDetailsReport() };
        const viewWilsonFileReport = { name: 'LotDetails.Summary.ViewWilsonReport', onClick: () => viewWilsonWarehouseReport() };
        const viewDailyPackageReportItem = { name: 'LotDetails.Summary.ViewDailyPackageReport', onClick: () => viewDailyPackageReport() };
        const viewLotDataReportItem = { name: 'Lot Data Report', onClick: () => viewLotDataReport() };

        switch (siteCode) {
            case "SCP":
                switch (businessUnitCode) {
                    case "SCP_CAT":
                        setReportItems([]);
                        break;
                    default:
                        setReportItems([viewReportItem]);
                }
                break;
            case "BAY":
                switch (businessUnitCode) {
                    case "BTBU":
                        setReportItems([
                            viewLotDetailsReportItem
                        ]);
                        break;
                    case "BAPP_PP":
                    default:
                        setReportItems([]);
                }
                break;
            case "HCC":
                setReportItems([
                    viewReportItem,
                    baggingReportItem,
                ]);
                break;
            case "BTR":
                switch (businessUnitCode) {
                    case "BRPO":
                        setReportItems([viewLotDetailsReportItem]);
                        break;
                    case "BRCP_HFU":
                    case "BRCP_VFU":
                    case "BRPP_G_LINE":
                        setReportItems(
                            currentStatus === "Graded"
                                ? [viewLotDetailsReportItem, viewDailyPackageReportItem, viewLotDataReportItem, viewWilsonFileReport]
                                : [viewLotDetailsReportItem, viewDailyPackageReportItem, viewLotDataReportItem]);
                        break;
                }
                break;
            default:
                setReportItems([]);
        }
    }, [siteCode, currentStatus, admin, businessUnitCode, viewLotReport, viewBaggingReport, viewLotDetailsReport, viewWilsonWarehouseReport]);

    if (reportItems.length === 0) {
        return <></>;
    }

    if (reportItems.length === 1) {
        const reportItem = reportItems[0];
        return (
            <Button disabled={disabled || loadingReport} onClick={reportItem.onClick}>
                {t<string>(reportItem.name)}
                {
                    loadingReport &&
                    <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="ms-2"
                    />
                }
            </Button>
        );
    }

    return (
        <SplitButton variant="primary"
            title={
                <>
                    {t<string>('LotDetails.Summary.ViewReport')}
                    {
                        loadingReport &&
                        <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="ms-2"
                        />
                    }
                </>
            }
            onClick={reportItems[0].onClick}
            disabled={disabled || loadingReport}>
            {reportItems.map(i => <Dropdown.Item key={`${i.name}-report-option`} onClick={i.onClick}>{t<string>(i.name)}</Dropdown.Item>)}
        </SplitButton>
    );
}

export default ViewReportButton;